import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FaBolt,
  FaDumbbell,
  FaArrowRight,
  FaUsers,
  FaLock,
  FaShower,
  FaUserShield,
  FaCheckCircle,
  FaMapMarkerAlt,
  FaSearch,
  FaTimes,
  FaSlidersH,
  FaBullseye,
  FaLightbulb,
  FaTrophy,
  FaWhatsapp,
} from 'react-icons/fa';
import { Link } from 'react-router-dom';

/* ─── Import Equipment Images from Assets ─── */
import treadmillImg from '../assets/treadmill.png';
import spinBikesImg from '../assets/Spin-Bikes.png';
import squatRackImg from '../assets/Olympic-Standard.png';
import benchStationImg from '../assets/Bench-Stations.png';
import cableCrossoverImg from '../assets/Dual-Pulley-Cable.png';
import smithMachineImg from '../assets/Guided-Path-Smith-Machine.png';
import legPressImg from '../assets/Leg-Press-Hack-Squat-Combo.png';
import latPulldownImg from '../assets/Lat-Pulldown.png';
import dumbbellsImg from '../assets/Rubber-Coated-Hex-Dumbbells.png';
import kettlebellsImg from '../assets/Kettlebells.png';

/* ─── Rich Equipment Arsenal Data ─── */
const equipmentItems = [
  {
    id: 'treadmill',
    name: 'Commercial-Grade Treadmills',
    category: 'Cardio',
    tag: 'Cardio',
    image: treadmillImg,
    zone: 'Cardio Suite',
    rating: '5.0',
    intensity: 'High Calorie Burn',
    targetMuscles: ['Calves', 'Hamstrings', 'Quadriceps', 'Cardiovascular Engine'],
    description:
      'Heavy-duty motorized commercial treadmills with multi-layer orthopedic cushioning decks, motorized incline gradients, and instant telemetry heart-rate tracking for safe, high-burn aerobic conditioning.',
    specs: [
      { label: 'Incline Range', value: '0% – 15% Motorized Gradient' },
      { label: 'Speed Capacity', value: 'Up to 20 km/h Precision Drive' },
      { label: 'Deck Technology', value: 'Multi-Layer Orthopedic Shock Absorption' },
      { label: 'Live Telemetry', value: 'Heart Rate, Distance, Calories, Pace' },
    ],
    coachingTip: 'Perform HIIT intervals (30s sprint / 30s walk) for maximum post-exercise calorie burn.',
  },
  {
    id: 'spin-bikes',
    name: 'Indoor Spin Bikes & Ellipticals',
    category: 'Cardio',
    tag: 'Cardio',
    image: spinBikesImg,
    zone: 'Cardio Suite',
    rating: '4.9',
    intensity: 'Low Impact / High Burn',
    targetMuscles: ['Glutes', 'Quadriceps', 'Core Stability', 'Cardio Endurance'],
    description:
      'Precision magnetic-resistance studio spin bikes and low-impact elliptical cross-trainers designed for intense calorie burning without placing stressful impact on joints, hips, and knees.',
    specs: [
      { label: 'Resistance', value: 'Whisper-Quiet Magnetic Flywheel System' },
      { label: 'Adjustability', value: '4-Way Saddle & Ergonomic Multi-Grips' },
      { label: 'Joint Safety', value: 'Zero Impact Biomechanical Stride' },
      { label: 'Console', value: 'Live Cadence (RPM), Watts, Heart Rate' },
    ],
    coachingTip: 'Ideal for warm-ups, active recovery days, and high-cadence spin sprint intervals.',
  },
  {
    id: 'squat-racks',
    name: 'Olympic-Standard Squat Racks',
    category: 'Strength & Power',
    tag: 'Strength',
    image: squatRackImg,
    zone: 'Heavy Power Pit',
    rating: '5.0',
    intensity: 'Maximum Strength',
    targetMuscles: ['Quadriceps', 'Gluteus Maximus', 'Lower Back', 'Core Bracing'],
    description:
      'Competition-grade steel squat cages engineered with laser-cut adjustment holes, heavy-duty J-hooks, and extended safety catch bars for heavy back squats, front squats, and overhead presses.',
    specs: [
      { label: 'Frame Material', value: 'Heavy 11-Gauge Tubular Structural Steel' },
      { label: 'Safety Catch', value: 'Reinforced Extended Spotter Safety Arms' },
      { label: 'Load Capacity', value: 'Tested up to 600+ kg Weight Loads' },
      { label: 'Pull-Up Rig', value: 'Multi-Grip Diamond-Knurled Pull-Up Bar' },
    ],
    coachingTip: 'Engage your latissimus dorsi and brace your abdominal wall tightly before descending into each rep.',
  },
  {
    id: 'bench-stations',
    name: 'Flat, Incline & Decline Bench Stations',
    category: 'Strength & Power',
    tag: 'Strength',
    image: benchStationImg,
    zone: 'Heavy Power Pit',
    rating: '4.9',
    intensity: 'Chest & Pressing Power',
    targetMuscles: ['Pectoralis Major/Minor', 'Triceps Brachii', 'Anterior Deltoids'],
    description:
      'Tri-angle commercial bench press stations reinforced for heavy powerlifting, featuring dense tear-resistant padding and wide spotting safety brackets for secure solo and spotter lifting.',
    specs: [
      { label: 'Configuration', value: 'Flat, Incline (30°/45°) & Decline' },
      { label: 'Padding', value: 'High-Density Tear-Resistant Double Stitched' },
      { label: 'Spotter Step', value: 'Integrated Diamond-Plate Safety Stand' },
      { label: 'Weight Rating', value: '500+ kg Commercial Load Limit' },
    ],
    coachingTip: 'Keep your shoulder blades retracted and depressed to protect your rotator cuffs during heavy presses.',
  },
  {
    id: 'cable-crossover',
    name: 'Dual-Pulley Cable Crossover System',
    category: 'Cable Systems',
    tag: 'Cables',
    image: cableCrossoverImg,
    zone: 'Isolation & Cable Bay',
    rating: '5.0',
    intensity: 'Constant Muscle Tension',
    targetMuscles: ['Chest Flyes', 'Biceps/Triceps', 'Deltoids', 'Rotator Cuffs'],
    description:
      'Twin-tower multi-station cable crossover with 360-degree swivel pulleys and micro-adjustable height settings that provide constant resistance throughout every millimeter of movement.',
    specs: [
      { label: 'Pulley System', value: '360° Free-Rotating Swivel Pulleys' },
      { label: 'Weight Stacks', value: 'Twin Dual Independent Stacks (100 kg each)' },
      { label: 'Cable Type', value: 'Aircraft-Grade 4.8mm Nylon-Coated Wire' },
      { label: 'Positions', value: '24 Laser-Numbered Vertical Adjustments' },
    ],
    coachingTip: 'Maintain a slight bend in your elbows and squeeze hard at peak contraction for maximum muscle fiber recruitment.',
  },
  {
    id: 'smith-machine',
    name: 'Guided-Path 3D Smith Machine',
    category: 'Strength & Safety',
    tag: 'Strength',
    image: smithMachineImg,
    zone: 'Strength Bay',
    rating: '4.8',
    intensity: 'Controlled Hypertrophy',
    targetMuscles: ['Shoulders', 'Quadriceps', 'Chest', 'Trapezius'],
    description:
      'Ultra-smooth linear-bearing smith machine with counterbalanced barbell and multi-stop rotational lockouts for heavy, stabilized pressing, squatting, and shrugging without needing a spotter.',
    specs: [
      { label: 'Guide Rails', value: 'Precision Hardened Steel Linear Bearings' },
      { label: 'Bar Counterbalance', value: 'Neutral Starting Weight Mechanism' },
      { label: 'Lockout Safety', value: 'Quick-Turn Catch Hooks every 15 cm' },
      { label: 'Plate Storage', value: '6 Integrated Olympic Plate Storage Pegs' },
    ],
    coachingTip: 'Great for strict overhead shoulder presses, Romanian deadlifts, and Bulgarian split squats.',
  },
  {
    id: 'leg-press',
    name: '45° Heavy Leg Press & Hack Squat',
    category: 'Leg Essentials',
    tag: 'Legs',
    image: legPressImg,
    zone: 'Leg Development Zone',
    rating: '5.0',
    intensity: 'Maximum Lower Body Power',
    targetMuscles: ['Quadriceps', 'Glutes', 'Hamstrings', 'Calves'],
    description:
      'Dual-function plate-loaded 45-degree angled leg press and hack squat platform engineered for extreme lower body loading while relieving spinal lumbar compression.',
    specs: [
      { label: 'Track Angle', value: 'Precision 45° Linear Incline Track' },
      { label: 'Foot Platform', value: 'Oversized Multi-Angle Diamond Plate' },
      { label: 'Safety Catches', value: 'Dual Spring-Loaded Mechanical Stops' },
      { label: 'Load Capacity', value: 'Up to 800 kg Plate Loading Capacity' },
    ],
    coachingTip: 'Never lock your knees out completely at the top of the press to maintain continuous tension and protect joint cartilage.',
  },
  {
    id: 'lat-pulldown',
    name: 'Lat Pulldown & Seated Row Station',
    category: 'Back Training',
    tag: 'Back',
    image: latPulldownImg,
    zone: 'Back & Pull Zone',
    rating: '4.9',
    intensity: 'V-Taper Hypertrophy',
    targetMuscles: ['Latissimus Dorsi', 'Rhomboids', 'Middle/Lower Traps', 'Biceps'],
    description:
      'Heavy-duty combination back station offering overhead lat pulldown and low seated cable rows with adjustable contoured thigh rollers for building wide, thick back architecture.',
    specs: [
      { label: 'Dual Functionality', value: 'Overhead Wide Lat Pulldown & Low Row' },
      { label: 'Thigh Lockdown', value: 'Adjustable Contoured High-Density Rollers' },
      { label: 'Weight Stack', value: '110 kg Cast-Iron Precision Stack' },
      { label: 'Attachments', value: 'Mag-Grip, Straight Bar, V-Bar Included' },
    ],
    coachingTip: 'Lead the movement with your elbows and arch your upper back slightly to achieve complete lat contraction.',
  },
  {
    id: 'dumbbells',
    name: 'Rubber-Coated Hex Dumbbells (2.5–50 kg)',
    category: 'Free Weights',
    tag: 'Free Weights',
    image: dumbbellsImg,
    zone: 'Free Weight Arena',
    rating: '5.0',
    intensity: 'Full-Body Free Strength',
    targetMuscles: ['Full Body', 'Unilateral Balance', 'Stabilizers', 'Forearms'],
    description:
      'Complete tournament-grade hex dumbbell rack spanning 2.5 kg to 50 kg pairs. Featuring textured chrome knurled handles and virgin rubber heads that eliminate rolling and protect floors.',
    specs: [
      { label: 'Weight Increments', value: '2.5 kg to 50 kg Complete Full Pairs' },
      { label: 'Head Construction', value: 'Anti-Roll Hexagonal Anti-Impact Rubber' },
      { label: 'Handle Grip', value: 'Ergonomic Diamond-Pattern Chrome Knurling' },
      { label: 'Storage', value: 'Heavy 3-Tier Commercial Angled Saddled Racks' },
    ],
    coachingTip: 'Use dumbbells for unilateral bench and shoulder presses to ensure equal muscle development on both sides.',
  },
  {
    id: 'kettlebells',
    name: 'Competition Kettlebells & Slam Balls',
    category: 'Functional Fitness',
    tag: 'Functional',
    image: kettlebellsImg,
    zone: 'Functional Turf Zone',
    rating: '4.9',
    intensity: 'Explosive Athletic Power',
    targetMuscles: ['Posterior Chain', 'Cardio Core', 'Rotational Power', 'Grip'],
    description:
      'Color-coded uniform competition steel kettlebells paired with reinforced burst-proof dead-bounce slam balls for high-octane HIIT circuits, explosive hips, and core strength.',
    specs: [
      { label: 'Kettlebells', value: 'Uniform Standard Competition Steel Shells' },
      { label: 'Slam Balls', value: 'Heavy-Duty Dead-Bounce Textured Shells (5–20 kg)' },
      { label: 'Handle Finish', value: 'Raw Unpainted Steel for Optimal Chalk Grip' },
      { label: 'Standard', value: 'International Kettlebell Sport Color-Coded' },
    ],
    coachingTip: 'Focus on explosive hip drive rather than pulling with your arms during kettlebell swings and snatches.',
  },
];

const categories = ['All', 'Cardio', 'Strength', 'Cables', 'Legs', 'Back', 'Free Weights', 'Functional'];

const specialFacilities = [
  {
    icon: <FaUsers className="text-3xl text-red-500" />,
    title: 'Unisex Training Floor',
    highlight: 'Spacious & Inclusive',
    description:
      'A welcoming, climate-controlled, and motivating training environment where beginners to elite athletes train comfortably side by side.',
    color: '#e51b24',
  },
  {
    icon: <FaLock className="text-3xl text-red-400" />,
    title: 'Personal Locker Storage',
    highlight: 'Keyless & Secure',
    description:
      'Digital combination access lockers ensuring your personal belongings and gym gear are safely stored while you push your workout limits.',
    color: '#3498db',
  },
  {
    icon: <FaShower className="text-3xl text-green-400" />,
    title: 'Changing Rooms & Showers',
    highlight: 'Hygienic & Refreshing',
    description:
      'Spotlessly sanitized private changing rooms and high-pressure hot water showers so you can head straight to work or meetings fresh.',
    color: '#27ae60',
  },
  {
    icon: <FaUserShield className="text-3xl text-red-500" />,
    title: 'On-Floor Coaching & Spotting',
    highlight: 'Safety & Form First',
    description:
      'Certified trainers present on the gym floor 24/6 to correct biomechanics, spot heavy lifts, and ensure you train without injury risk.',
    color: '#e51b24',
  },
];

/* ─── Zig-Zag Showcase Item Component ─── */
const EquipmentZigZagRow = ({ eq, index }) => {
  const [activeTab, setActiveTab] = useState('specs'); // 'specs', 'muscles', 'coach'
  const isEven = index % 2 === 0;

  return (
    <motion.div
      className={`group relative bg-gradient-to-b from-[#181a24]/95 via-[#14161f]/95 to-[#101218]/95 backdrop-blur-2xl rounded-3xl border border-white/10 overflow-hidden hover:border-red-500/60 transition-all duration-500 shadow-2xl p-6 sm:p-8 lg:p-10 flex flex-col ${
        isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'
      } items-center gap-8 lg:gap-12`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.05 }}
      viewport={{ once: true, margin: '-50px' }}
    >
      {/* Top Accent Strip */}
      <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-red-600 via-rose-500 to-red-700" />

      {/* ── 1. IMAGE SHOWROOM BAY (LEFT OR RIGHT ZIG-ZAG) ── */}
      <div className="w-full lg:w-1/2 flex-shrink-0">
        <div className="relative h-72 sm:h-96 w-full rounded-2xl bg-radial from-[#1e222c] to-[#0c0e12] flex items-center justify-center p-6 overflow-hidden border border-white/10 shadow-inner">
          {/* Ambient Lighting Ring */}
          <div className="absolute w-64 h-64 sm:w-80 sm:h-80 rounded-full bg-red-600/15 blur-[70px] pointer-events-none group-hover:bg-red-600/30 transition-all duration-500" />

          {/* High-Res Asset Image */}
          <img
            src={eq.image}
            alt={eq.name}
            className="relative z-10 w-full h-full object-contain object-center drop-shadow-[0_20px_35px_rgba(0,0,0,0.85)] group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />

          {/* Floating Badges */}
          <div className="absolute top-4 left-4 z-20 flex flex-wrap gap-2">
            <span className="bg-red-600/90 backdrop-blur-md text-white text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full shadow-lg border border-white/20">
              {eq.category}
            </span>
            <span className="bg-black/70 backdrop-blur-md text-gray-300 text-[10px] font-semibold uppercase px-2.5 py-1 rounded-full border border-white/10">
              {eq.zone}
            </span>
          </div>

          {/* Giant Watermark Number */}
          <span className="absolute top-3 right-4 text-5xl font-black text-white/[0.08] select-none font-mono">
            {String(index + 1).padStart(2, '0')}
          </span>

          {/* Intensity Pill */}
          <div className="absolute bottom-4 left-4 z-20">
            <span className="bg-[#12141a]/90 backdrop-blur-md text-red-400 text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full border border-red-500/30 flex items-center gap-1.5 shadow-lg">
              <FaBolt className="text-red-500 text-[9px]" /> {eq.intensity}
            </span>
          </div>
        </div>
      </div>

      {/* ── 2. CONTENT & PERFORMANCE MATRIX (OPPOSITE SIDE) ── */}
      <div className="w-full lg:w-1/2 flex flex-col justify-between gap-5">
        <div>
          {/* Header Title */}
          <div className="flex items-center gap-2 mb-2 text-xs font-bold text-red-500 uppercase tracking-widest">
            <span>{eq.zone}</span>
            <span>•</span>
            <span>Unit 1, 2, 3</span>
          </div>

          <h3 className="text-2xl sm:text-3xl font-black text-white group-hover:text-red-400 transition-colors leading-tight mb-3">
            {eq.name}
          </h3>

          <p className="text-gray-300 text-xs sm:text-sm leading-relaxed mb-4">
            {eq.description}
          </p>

          {/* ── INLINE TABS SELECTOR ── */}
          <div className="flex items-center gap-1.5 p-1 bg-[#0f1117] rounded-xl border border-white/10 mb-3">
            <button
              onClick={() => setActiveTab('specs')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                activeTab === 'specs'
                  ? 'bg-red-600 text-white shadow-md shadow-red-600/30'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <FaSlidersH className="text-[10px]" /> Machine Specs
            </button>
            <button
              onClick={() => setActiveTab('muscles')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                activeTab === 'muscles'
                  ? 'bg-red-600 text-white shadow-md shadow-red-600/30'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <FaBullseye className="text-[10px]" /> Target Muscles
            </button>
            <button
              onClick={() => setActiveTab('coach')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                activeTab === 'coach'
                  ? 'bg-red-600 text-white shadow-md shadow-red-600/30'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <FaLightbulb className="text-[10px]" /> Coach's Rule
            </button>
          </div>

          {/* ── TAB CONTENT DISPLAY AREA ── */}
          <div className="min-h-[145px] bg-[#10121a] rounded-2xl p-4 border border-white/10 flex flex-col justify-center shadow-inner">
            {activeTab === 'specs' && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className="grid grid-cols-1 sm:grid-cols-2 gap-2.5"
              >
                {eq.specs.map((item, sIdx) => (
                  <div key={sIdx} className="bg-white/5 p-2.5 rounded-xl border border-white/5 flex flex-col justify-between">
                    <span className="text-[10px] uppercase font-bold text-red-400 tracking-wider">
                      {item.label}
                    </span>
                    <span className="text-xs font-semibold text-gray-200 mt-1">
                      {item.value}
                    </span>
                  </div>
                ))}
              </motion.div>
            )}

            {activeTab === 'muscles' && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className="flex flex-col gap-2.5"
              >
                <span className="text-[10px] uppercase font-bold text-red-400 tracking-widest">
                  Primary Hypertrophy & Strength Target:
                </span>
                <div className="flex flex-wrap gap-2">
                  {eq.targetMuscles.map((muscle, mIdx) => (
                    <div
                      key={mIdx}
                      className="bg-red-600/15 border border-red-500/30 text-white text-xs px-3 py-1.5 rounded-xl font-semibold flex items-center gap-1.5 shadow-sm"
                    >
                      <FaCheckCircle className="text-red-500 text-[10px]" />
                      <span>{muscle}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {activeTab === 'coach' && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className="flex items-start gap-3.5"
              >
                <div className="w-9 h-9 rounded-xl bg-red-600/20 text-red-400 flex items-center justify-center flex-shrink-0 mt-0.5 text-base">
                  💡
                </div>
                <div className="flex flex-col">
                  <span className="text-[11px] font-bold text-red-400 uppercase tracking-wider mb-1">
                    Certified Trainer Form Rule:
                  </span>
                  <p className="text-xs sm:text-sm text-gray-200 leading-relaxed italic">
                    "{eq.coachingTip}"
                  </p>
                </div>
              </motion.div>
            )}
          </div>
        </div>

        {/* Action Row */}
        <div className="flex flex-wrap items-center justify-between gap-4 pt-3 border-t border-white/10">
          <div className="flex items-center gap-2 text-gray-400 text-xs">
            <FaMapMarkerAlt className="text-red-500 text-sm" />
            <span>Chettipalayam • Pappampatti Pirivu • Peedampalli</span>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="https://wa.me/917339504861?text=Hi%20I'm%20interested%20in%20talking%20to%20a%20coach%20about%20your%20training%20gear!"
              target="_blank"
              rel="noreferrer"
              className="bg-green-600/20 hover:bg-green-600 text-green-400 hover:text-white border border-green-500/30 px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2"
            >
              <FaWhatsapp className="text-sm" /> Talk to a Coach
            </a>
            <Link
              to="/contact"
              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-bold text-xs uppercase tracking-wider px-5 py-2.5 rounded-xl transition-all shadow-lg shadow-red-600/20 flex items-center gap-2 group/btn"
            >
              <span>Start Your Fitness Journey</span>
              <FaArrowRight className="text-[10px] group-hover/btn:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/* ─── Main Equipments Page Component ─── */
const Equipments = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter and Search logic
  const filtered = equipmentItems.filter((eq) => {
    const matchesCategory = activeFilter === 'All' || eq.tag === activeFilter;
    const matchesSearch =
      eq.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      eq.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
      eq.zone.toLowerCase().includes(searchQuery.toLowerCase()) ||
      eq.targetMuscles.some((m) => m.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <div className="flex flex-col w-full overflow-x-hidden min-h-screen">
      {/* ── SECTION 1: CINEMATIC SHOWROOM HERO ── */}
      <section className="relative text-center py-16 md:py-24 px-4 overflow-hidden border-b border-white/10">
        {/* Ambient Glows */}
        <div className="absolute w-[32rem] h-[32rem] bg-red-600/20 blur-[180px] rounded-full top-0 left-1/2 -translate-x-1/2 -z-10" />
        <div className="absolute w-80 h-80 bg-red-800/15 blur-[160px] rounded-full bottom-0 right-10 -z-10" />

        <div className="max-w-5xl mx-auto flex flex-col items-center">
          {/* Top Pill Badge */}
          <motion.div
            className="inline-flex items-center gap-2 bg-red-600/15 border border-red-500/30 px-5 py-2 rounded-full text-xs font-bold uppercase tracking-widest text-red-500 mb-6 backdrop-blur-md shadow-lg"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <FaTrophy className="text-red-500" />
            <span>IDOL FITNESS ZONE • Commercial Equipment Arsenal</span>
          </motion.div>

          {/* Heading */}
          <motion.h1
            className="text-4xl sm:text-6xl md:text-7xl font-black uppercase tracking-tight text-white mb-6 leading-none"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-transparent [-webkit-text-stroke:_1.5px_white]">WORLD-CLASS </span>
            <span className="text-red-500">TRAINING GEAR</span>
          </motion.h1>

          <motion.p
            className="text-gray-300 text-sm sm:text-lg leading-relaxed max-w-3xl mx-auto mb-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Engineered for pure biomechanical efficiency, extreme durability, and maximum hypertrophic stimulus. Explore the Olympic racks, cable towers, and heavy iron deployed across our 3 Coimbatore units.
          </motion.p>

          {/* Live Showroom Metrics */}
          <motion.div
            className="grid grid-cols-2 sm:grid-cols-4 gap-4 w-full max-w-4xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {[
              { label: 'Commercial Stations', val: '10+ Elite Machines' },
              { label: 'Dumbbell Heavy Rack', val: 'Up to 50 kg' },
              { label: 'Coimbatore Units', val: '3 Connected Gyms' },
              { label: 'Quality Standard', val: '100% Olympic Grade' },
            ].map((stat, idx) => (
              <div
                key={idx}
                className="bg-[#181a24]/90 backdrop-blur-xl border border-white/10 rounded-2xl p-4 text-center hover:border-red-500/40 transition-colors shadow-xl"
              >
                <span className="text-base sm:text-lg font-black text-white block">{stat.val}</span>
                <span className="text-[11px] text-red-400 uppercase font-semibold tracking-wider">{stat.label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 2: SEARCH & CATEGORY FILTER BAR ── */}
      <section className="px-4 md:px-12 py-8 max-w-7xl mx-auto w-full">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-6 bg-[#161820]/90 backdrop-blur-xl p-4 md:p-6 rounded-3xl border border-white/10 shadow-2xl">
          {/* Category Tabs */}
          <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 overflow-x-auto w-full lg:w-auto">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveFilter(cat)}
                className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-300 cursor-pointer border ${
                  activeFilter === cat
                    ? 'bg-red-600 border-red-500 text-white shadow-lg shadow-red-600/30 scale-105'
                    : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10 hover:text-white'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Search Input */}
          <div className="relative w-full lg:w-72">
            <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
            <input
              type="text"
              placeholder="Search gear or muscle..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white/5 border border-white/15 rounded-xl pl-9 pr-8 py-2 text-xs text-white placeholder-gray-400 focus:outline-none focus:border-red-500 transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <FaTimes className="text-xs" />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* ── SECTION 3: ZIG-ZAG SHOWCASE EQUIPMENT LIST ── */}
      <section className="px-4 md:px-12 py-6 max-w-7xl mx-auto w-full">
        {filtered.length === 0 ? (
          <div className="text-center py-20 bg-[#161820]/60 rounded-3xl border border-white/10">
            <FaDumbbell className="text-5xl text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">No matching equipment found</h3>
            <p className="text-gray-400 text-xs mb-6">Try searching for a different keyword or select "All".</p>
            <button
              onClick={() => {
                setActiveFilter('All');
                setSearchQuery('');
              }}
              className="bg-red-600 text-white text-xs font-bold uppercase px-6 py-2.5 rounded-xl cursor-pointer"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-10">
            {filtered.map((eq, i) => (
              <EquipmentZigZagRow key={eq.id} eq={eq} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* ── SECTION 4: SPECIAL FACILITIES SECTION ── */}
      <section className="relative px-4 md:px-12 py-20 text-white overflow-hidden">
        <div className="absolute w-80 h-80 bg-red-600/15 blur-[150px] rounded-full top-0 right-0 -z-10" />

        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="flex items-center gap-4 justify-center mb-4">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-red-600" />
            <span className="text-red-500 text-xs font-bold uppercase tracking-[0.3em]">
              Facility Amenities
            </span>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-red-600" />
          </div>
          <h2 className="text-3xl sm:text-5xl font-black uppercase tracking-wide mb-4">
            <span className="text-transparent [-webkit-text-stroke:_1.5px_white]">SPECIAL </span>
            <span>FACILITIES</span>
          </h2>
          <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
            World-class machinery is only part of the transformation. Our 3 gym facilities in Coimbatore provide high-grade convenience, hygiene, and coaching.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {specialFacilities.map((fac, i) => (
            <motion.div
              key={i}
              className="group relative bg-[#181a24]/90 backdrop-blur-xl p-7 rounded-3xl border border-white/10 hover:border-red-500/50 transition-all duration-300 overflow-hidden text-center shadow-xl"
              variants={fadeUp}
              whileHover={{ y: -8 }}
            >
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg group-hover:scale-110 transition-transform duration-300"
                style={{ backgroundColor: `${fac.color}20` }}
              >
                {fac.icon}
              </div>
              <span className="text-[10px] text-red-400 font-bold uppercase tracking-widest block mb-1">
                {fac.highlight}
              </span>
              <h4 className="text-lg font-bold mb-2 text-white group-hover:text-red-400 transition-colors uppercase">
                {fac.title}
              </h4>
              <p className="text-gray-400 text-xs leading-relaxed">{fac.description}</p>

              <div
                className="absolute bottom-0 left-0 w-full h-1 transition-all duration-300 group-hover:h-1.5"
                style={{ backgroundColor: fac.color }}
              />
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── SECTION 5: BOTTOM TRIAL CTA BANNER ── */}
      <section className="px-4 md:px-12 pb-16 max-w-6xl mx-auto w-full">
        <motion.div
          className="relative overflow-hidden rounded-3xl shadow-2xl bg-gradient-to-r from-red-700 via-red-600 to-rose-700 p-8 sm:p-12 flex flex-col sm:flex-row items-center justify-between gap-6 border border-white/20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div>
            <h3 className="text-2xl sm:text-4xl font-black uppercase tracking-tight mb-2 text-white">
              Try Out All Equipment Free
            </h3>
            <p className="text-white/90 text-xs sm:text-sm max-w-lg leading-relaxed">
              Book a 1-day complimentary workout pass at any of our 3 units: Chettipalayam JJ Nagar, Pappampatti Pirivu, or Peedampalli.
            </p>
          </div>
          <Link
            to="/contact"
            className="bg-white hover:bg-gray-100 text-black font-black px-8 py-4 rounded-xl transition-all shadow-xl flex items-center gap-3 text-xs uppercase tracking-wider flex-shrink-0 group"
          >
            <span>Start Your Fitness Journey</span>
            <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </section>
    </div>
  );
};

export default Equipments;
